import { Outlet, createFileRoute, notFound } from "@tanstack/react-router";
import { SiteFooter } from "#/components/SiteFooter.tsx";
import { SiteHeader } from "#/components/SiteHeader.tsx";
import type { Messages } from "#/i18n/index.ts";
import { LOCALES, localeFromParam, localizedPath, type Locale } from "#/lib/locale.ts";
import { SITE_ORIGIN } from "#/lib/site.ts";
import { getLayoutData, getMessages } from "#/server/functions.ts";
import { Route as RootRoute } from "#/routes/__root.tsx";

/**
 * Copy already fetched in this server process or browser tab, by locale. The
 * copy of a release never changes, so a tab fetches each locale at most once.
 */
const loadedMessages = new Map<Locale, Messages>();

/**
 * Locale layout route: resolves the optional locale prefix (404 for an unknown
 * one), fetches that locale's copy (never the other locales'), loads the
 * footer's version line, and sets the translated title and description, the
 * page's canonical URL and its URL in every locale. Of the query, only a page
 * number is kept: it selects different content (older changelog releases),
 * while filters and form input do not make a page of their own.
 */
export const Route = createFileRoute("/{-$locale}")({
  beforeLoad: async ({ params }) => {
    const locale = localeFromParam(params.locale);
    if (locale === undefined) throw notFound();
    const messages = loadedMessages.get(locale) ?? (await getMessages({ data: locale }));
    loadedMessages.set(locale, messages);
    return { locale, messages };
  },
  loader: () => getLayoutData(),
  head: ({ match, matches }) => {
    // The page's own path is the last match's (this layout's is only the locale
    // prefix), without the trailing slash an index route carries in the browser.
    const path = matches.reduce((_, leaf) => leaf.pathname, match.pathname).replace(/(.)\/+$/, "$1");
    const search = matches.reduce<object>((_, leaf) => leaf.search, match.search);
    const query = "page" in search && typeof search.page === "number" ? `?page=${String(search.page)}` : "";
    const url = (locale: Locale): string => `${SITE_ORIGIN}${localizedPath(path, locale)}${query}`;
    return {
      meta: [
        { title: match.context.messages.meta.title },
        { name: "description", content: match.context.messages.meta.description },
      ],
      links: [
        { rel: "canonical", href: url(match.context.locale) },
        ...LOCALES.map((locale) => ({ rel: "alternate", hrefLang: locale, href: url(locale) })),
        { rel: "alternate", hrefLang: "x-default", href: url("en") },
      ],
    };
  },
  component: LocaleLayout,
});

/**
 * Chrome shared by every page in a locale: header, main landmark and footer.
 *
 * @returns The page frame around the matched child route.
 */
function LocaleLayout() {
  const { locale, messages } = Route.useRouteContext();
  // The server rendered this locale's copy into the page; remembering it here
  // means navigating within the locale never fetches it again.
  loadedMessages.set(locale, messages);
  const layout = Route.useLoaderData();
  const { theme } = RootRoute.useLoaderData();
  return (
    <>
      <SiteHeader locale={locale} messages={messages} theme={theme} />
      <main id="content" tabIndex={-1}>
        <Outlet />
      </main>
      <SiteFooter
        locale={locale}
        messages={messages}
        data={layout.pmVersion === "" ? null : layout}
      />
    </>
  );
}
